import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Ban,
  CreditCard,
  Crown,
  Flame,
  ShoppingBag,
  Sparkles,
  UserPlus,
} from 'lucide-react';
import { TrendChart } from '../../components/TrendChart';
import { Button, Card, CardHeader, ErrorNote } from '../../components/ui';
import { api } from '../../lib/api';
import {
  formatDay,
  formatNumber,
  formatRelative,
  timezoneOffsetMinutes,
} from '../../lib/format';
import type {
  AppKey,
  CountryPeriodOverview,
  CountryPeriodStats,
  InsightPeriod,
} from '../../lib/types';
import { usersLink } from '../../lib/user-links';
import { ViewSkeleton } from './AllTime';
import {
  CountryTable,
  GrowthValue,
  InsightTile,
  ShareBar,
  countryName,
  flagFor,
  formatGrowth,
  formatPercent,
  growth,
  maxBy,
  percent,
  type CountryColumn,
} from './shared';

interface PeriodRow extends CountryPeriodStats {
  name: string;
  flag: string;
  /** Share of the period's new devices. */
  share: number;
  new_growth: number | null;
  purchase_growth: number | null;
  /** Of the devices that registered in the period, the share premium now. */
  premium_rate: number;
  ios_share: number;
}

const COLUMNS: CountryColumn<PeriodRow>[] = [
  {
    key: 'new_devices',
    label: 'New',
    title: 'Devices registered in the period, and share of all new',
    sortValue: (row) => row.new_devices,
    render: (row, maxShare) => (
      <>
        <span className="font-medium text-ink">
          {formatNumber(row.new_devices)}
        </span>
        <ShareBar share={row.share} max={maxShare} />
      </>
    ),
  },
  {
    key: 'new_growth',
    label: 'vs prev',
    title: 'New devices compared with the previous period',
    sortValue: (row) => row.new_growth,
    render: (row) => <GrowthValue value={row.new_growth} />,
  },
  {
    key: 'purchases',
    label: 'Purchases',
    title: 'Store purchases recorded in the period',
    sortValue: (row) => row.purchases,
    render: (row) => formatNumber(row.purchases),
  },
  {
    key: 'purchase_growth',
    label: 'vs prev',
    title: 'Purchases compared with the previous period',
    sortValue: (row) => row.purchase_growth,
    render: (row) => <GrowthValue value={row.purchase_growth} />,
  },
  {
    key: 'renewals',
    label: 'Renewals',
    title: 'Paid renewals recorded in the period',
    sortValue: (row) => row.renewals,
    render: (row) => formatNumber(row.renewals),
  },
  {
    key: 'trials_started',
    label: 'Trials',
    title: 'Trials started in the period',
    sortValue: (row) => row.trials_started,
    render: (row) => formatNumber(row.trials_started),
  },
  {
    key: 'cancels',
    label: 'Cancels',
    title: 'Auto-renew turned off in the period',
    sortValue: (row) => row.cancels,
    render: (row) => formatNumber(row.cancels),
  },
  {
    key: 'premium_rate',
    label: 'New → premium',
    title: "Of the period's new devices, the share that is premium now",
    sortValue: (row) => (row.new_devices > 0 ? row.premium_rate : null),
    render: (row) =>
      row.new_devices > 0 ? formatPercent(row.premium_rate) : '—',
  },
  {
    key: 'ios_share',
    label: 'iOS',
    title: "Share of the period's new devices on iOS",
    sortValue: (row) => (row.new_devices > 0 ? row.ios_share : null),
    render: (row) => (row.new_devices > 0 ? formatPercent(row.ios_share) : '—'),
  },
];

const PREVIOUS_LABEL: Record<InsightPeriod, string> = {
  day: 'the day before',
  week: 'the week before',
  month: 'the month before',
};

/**
 * One day, week or month by country, against the period before it.
 *
 * Every view of a period is one request: the backend answers it with two
 * range scans bounded by the period, and caches a finished period for half an
 * hour — so stepping back and forth through past days is mostly cache hits on
 * both sides.
 */
export function PeriodView({
  appKey,
  period,
  date,
  onOpenDay,
}: {
  appKey: AppKey | undefined;
  period: InsightPeriod;
  date: string;
  onOpenDay: (date: string) => void;
}) {
  const stats = useQuery({
    queryKey: ['countries-period', appKey ?? null, period, date],
    queryFn: () =>
      api.get<CountryPeriodOverview>('/api/insights/countries/period', {
        period,
        date,
        app_key: appKey,
        tz_offset: timezoneOffsetMinutes(),
      }),
    // A finished period barely moves; the running one is refreshed upstream
    // every couple of minutes.
    staleTime: 2 * 60_000,
    placeholderData: (previous) => previous,
  });

  const rows = useMemo(
    () => (stats.data ? toRows(stats.data) : []),
    [stats.data],
  );

  if (stats.isError) return <ErrorNote error={stats.error} />;
  if (stats.isPending) return <ViewSkeleton />;

  const data = stats.data;
  const range = { registered_from: data.from, registered_to: data.to };
  const best = maxBy(data.daily, (point) => point.new_devices);

  return (
    <div className="space-y-4">
      <p className="text-sm text-ink-2">
        <span className="font-medium text-ink">{periodLabel(data)}</span>
        {data.is_partial
          ? ` · in progress, compared with the same point of ${PREVIOUS_LABEL[period]}`
          : ` · compared with ${PREVIOUS_LABEL[period]}`}
      </p>

      <Insights data={data} rows={rows} appKey={appKey} />

      {period !== 'day' && data.daily.length > 1 && (
        <Card>
          <CardHeader
            title="Day by day"
            description={
              best && best.new_devices > 0
                ? `Best day: ${formatDay(best.date)} · ${formatNumber(best.new_devices)} new devices`
                : 'No registrations in this period'
            }
            action={
              best &&
              best.new_devices > 0 && (
                <Button
                  variant="ghost"
                  className="text-xs"
                  onClick={() => onOpenDay(best.date)}
                >
                  Open {formatDay(best.date)}
                </Button>
              )
            }
          />
          <div className="p-4 sm:p-5">
            <TrendChart
              data={data.daily.map((point) => ({
                date: point.date,
                new_users: point.new_devices,
                new_subscriptions: point.purchases + point.renewals,
              }))}
            />
          </div>
        </Card>
      )}

      <CountryTable
        title="By country"
        description={`Updated ${formatRelative(data.generated_at)}`}
        rows={rows}
        columns={COLUMNS}
        // Exactly the devices the "New" column counts.
        linkFor={(row) =>
          row.country
            ? usersLink({ app_key: appKey, country: row.country, ...range })
            : undefined
        }
        mobileValue={(row) => formatNumber(row.new_devices)}
        mobileSummary={(row) =>
          `${formatNumber(row.new_devices)} new (${formatGrowth(row.new_growth)}) · ` +
          `${formatNumber(row.purchases)} purchases (${formatGrowth(row.purchase_growth)}) · ` +
          `${formatNumber(row.trials_started)} trials · ${formatNumber(row.cancels)} cancels`
        }
      />
    </div>
  );
}

function Insights({
  data,
  rows,
  appKey,
}: {
  data: CountryPeriodOverview;
  rows: PeriodRow[];
  appKey: AppKey | undefined;
}) {
  const { totals } = data;
  const known = rows.filter((row) => row.country !== null);
  const range = { registered_from: data.from, registered_to: data.to };
  const link = (country: string) =>
    usersLink({ app_key: appKey, country, ...range });

  const topNew = maxBy(known, (row) => row.new_devices);
  const topBuyer = maxBy(known, (row) => row.purchases);
  // Small numbers swing wildly in percent; demand a real base on both sides.
  const fastestGrowing = maxBy(
    known.filter((row) => row.new_devices >= 5 && row.new_prev >= 3),
    (row) => row.new_growth ?? -Infinity,
  );
  const previous = PREVIOUS_LABEL[data.period];

  return (
    <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-3">
      <InsightTile
        icon={<UserPlus size={16} />}
        label="New devices"
        value={formatNumber(totals.new_devices)}
        caption={`${formatGrowth(growth(totals.new_devices, totals.new_prev))} vs ${previous} (${formatNumber(totals.new_prev)})`}
        to={usersLink({ app_key: appKey, ...range })}
        tone="brand"
      />
      <InsightTile
        icon={<CreditCard size={16} />}
        label="Purchases"
        value={formatNumber(totals.purchases)}
        caption={`${formatGrowth(growth(totals.purchases, totals.purchases_prev))} vs ${previous} · ${formatNumber(totals.renewals)} renewals`}
        // The event filter has a start but no end, so it's exact only while
        // the period is still running.
        to={
          data.is_partial
            ? usersLink({
                app_key: appKey,
                subscription_event: 'purchase',
                subscription_event_from: data.from,
              })
            : undefined
        }
      />
      <InsightTile
        icon={<Sparkles size={16} />}
        label="Trials started"
        value={formatNumber(totals.trials_started)}
        caption={
          <>
            <Ban size={11} className="mr-1 inline align-[-1px]" />
            {formatNumber(totals.cancels)} cancellations ·{' '}
            {formatPercent(percent(totals.premium, totals.new_devices))} of new
            devices premium now
          </>
        }
      />
      <InsightTile
        icon={<Crown size={16} />}
        label="Top country"
        value={
          topNew && topNew.new_devices > 0
            ? `${topNew.flag} ${topNew.name}`
            : '—'
        }
        caption={
          topNew && topNew.new_devices > 0
            ? `${formatNumber(topNew.new_devices)} new · ${formatPercent(topNew.share)} of all new`
            : 'No registrations in this period'
        }
        to={topNew?.country && topNew.new_devices > 0 ? link(topNew.country) : undefined}
      />
      <InsightTile
        icon={<Flame size={16} />}
        label="Fastest growing"
        value={
          fastestGrowing
            ? `${fastestGrowing.flag} ${formatGrowth(fastestGrowing.new_growth)}`
            : '—'
        }
        caption={
          fastestGrowing
            ? `${fastestGrowing.name} · ${formatNumber(fastestGrowing.new_devices)} new vs ${formatNumber(fastestGrowing.new_prev)}`
            : 'Not enough registrations to compare'
        }
        to={fastestGrowing?.country ? link(fastestGrowing.country) : undefined}
      />
      <InsightTile
        icon={<ShoppingBag size={16} />}
        label="Top buyers"
        value={
          topBuyer && topBuyer.purchases > 0
            ? `${topBuyer.flag} ${topBuyer.name}`
            : '—'
        }
        caption={
          topBuyer && topBuyer.purchases > 0
            ? `${formatNumber(topBuyer.purchases)} purchases · ${formatPercent(percent(topBuyer.purchases, totals.purchases))} of all`
            : 'No purchases in this period'
        }
        to={topBuyer?.country && topBuyer.purchases > 0 ? link(topBuyer.country) : undefined}
      />
    </div>
  );
}

const dayLabel = new Intl.DateTimeFormat(undefined, {
  weekday: 'long',
  month: 'long',
  day: 'numeric',
  year: 'numeric',
});
const shortDay = new Intl.DateTimeFormat(undefined, {
  month: 'short',
  day: 'numeric',
});
const monthLabel = new Intl.DateTimeFormat(undefined, {
  month: 'long',
  year: 'numeric',
});

/**
 * The period in words. `from` is the operator's local midnight — the request
 * carried this browser's offset — so local date parts read back correctly.
 */
function periodLabel(data: CountryPeriodOverview): string {
  const from = new Date(data.from);
  switch (data.period) {
    case 'day':
      return dayLabel.format(from);
    case 'week': {
      const last = new Date(Date.parse(data.to) - 86_400_000);
      return `${shortDay.format(from)} – ${shortDay.format(last)}, ${last.getFullYear()}`;
    }
    case 'month':
      return monthLabel.format(from);
  }
}

function toRows(data: CountryPeriodOverview): PeriodRow[] {
  const total = data.totals.new_devices;
  return data.countries.map((row) => ({
    ...row,
    name: countryName(row.country),
    flag: flagFor(row.country),
    share: percent(row.new_devices, total),
    new_growth: growth(row.new_devices, row.new_prev),
    purchase_growth: growth(row.purchases, row.purchases_prev),
    premium_rate: percent(row.premium, row.new_devices),
    ios_share: percent(row.ios, row.new_devices),
  }));
}
